import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslations, useLocale, useFormatter } from 'next-intl';
import NextLink from 'next/link';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from 'react-accessible-accordion';

import Container from '../../layout/container/container';
import Copy from '../../atoms/copy/copy';
import Headline from '../../atoms/headline/headline';
import AngleDown from '../../icons/angleDown.svg';
import InfoIcon from '../../icons/infoIcon.svg';
import StyledCurrentInfo from './currentInfo.style';
import MixedCopy from '../mixed-copy/mixedCopy';
import ArrowLink from '../arrow-link/arrowLink';
import StickyContainer from './stickyContainer';

const CurrentInfo = props => {
  const {
    data = {
      id: '',
      includeTours: true
    },
    headline,
    copy,
    infoCopy,
    hours,
    events,
    relevantLinks,
    toursFreeEntrance,
    toursHeadline,
    toursCtaLabel,
    toursCtaLink,
    address
  } = props;

  const [isOpen, setOpen] = useState(false);
  const hasHours = hours && hours.length > 0;

  const t = useTranslations();
  const locale = useLocale();
  const format = useFormatter();

  const weekday = {
    de: {
      Mon: 'Montag',
      Tue: 'Dienstag',
      Wed: 'Mittwoch',
      Thu: 'Donnerstag',
      Fri: 'Freitag',
      Sat: 'Samstag',
      Sun: 'Sonntag'
    },
    en: {
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
      Sun: 'Sunday'
    }
  };

  const getTodaysOpening = () => {
    let openToday = null;

    if (hours[0].open) {
      openToday = `${hours[0].from} - ${hours[0].to} ${locale === 'de' ? 'Uhr' : ''}`;
    }

    return openToday;
  };

  const getHours = hour => {
    let openedHours = null;

    if (hour.open) {
      openedHours = (
        <li key={hour.day}>
          <span>{weekday[locale][hour.day]}</span>
          <span>
            {hour.from} - {hour.to}
          </span>
        </li>
      );
    } else {
      openedHours = (
        <li key={hour.day}>
          <span>{weekday[locale][hour.day]}</span>
          {t('currentInfo.closed')}
        </li>
      );
    }

    return openedHours;
  };

  const renderOpeningHours = () => (
    <Accordion allowZeroExpanded accordion={false} onChange={uids => setOpen(uids.length > 0)}>
      <AccordionItem className="accordion__item">
        <AccordionItemHeading>
          <AccordionItemButton
            aria-label={
              isOpen ? t('currentInfo.closeOpeningHours') : t('currentInfo.openOpeningHours')
            }
          >
            {isOpen ? (
              t('currentInfo.openingHours')
            ) : (
              <>
                {getTodaysOpening() ? (
                  <>
                    <span className="current-info__opened">{t('currentInfo.opened')}:</span>
                    <span className="current-info__todays-openening">{getTodaysOpening()}</span>
                  </>
                ) : (
                  t('currentInfo.closedToday')
                )}
              </>
            )}
            <AngleDown
              role="img"
              aria-label={
                isOpen ? t('currentInfo.closeOpeningHours') : t('currentInfo.openOpeningHours')
              }
            />
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <ul className="current-info__hours">{hours.map(hour => getHours(hour))}</ul>
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>
  );

  return (
    <Container large id={data.id ?? ''}>
      <StyledCurrentInfo {...props} isClosed={!hasHours} aria-label={headline || ''}>
        <div className="current-info__container">
          <div className="current-info__base-container intro">
            <div className="current-info__base-first-col">
              {headline && <Headline primary>{headline}</Headline>}
              <div className="current-info__opening-hours">
                {hasHours ? (
                  renderOpeningHours(hours)
                ) : (
                  <span className="current-info__closed">Geschlossen</span>
                )}
              </div>
            </div>
            <div className="current-info__base-second-col">
              {copy && <Copy>{copy}</Copy>}
              {infoCopy && (
                <div className="current-info__info-box">
                  <div className="current-info__info-icon">
                    <InfoIcon role="img" ariaLabel="Info Icon" />
                  </div>
                  <MixedCopy className="current-info__info-content" fullWidth html={infoCopy} />
                </div>
              )}
            </div>
          </div>
          <StickyContainer
            relevantLinks={relevantLinks}
            address={address}
            toursFreeEntrance={toursFreeEntrance}
          />
          {data.includeTours && hasHours && (
            <div className="current-info__base-container guided-tours">
              <div className="current-info__base-first-col">
                {toursHeadline && <Headline secondary>{toursHeadline}</Headline>}
                {toursCtaLink && (
                  <div className="current-info__more-events">
                    <ArrowLink
                      href={toursCtaLink}
                      srTitle={toursHeadline ? t('learnMoreAbout') + toursHeadline : t('learnMore')}
                      shortArrow
                      size="30"
                      title={toursCtaLabel ?? ''}
                    />
                  </div>
                )}
              </div>
              {events && events.length > 0 && (
                <div className="current-info__base-second-col">
                  <div className="current-info__events-list">
                    {events.slice(0, 3).map(event => {
                      if (!event.uri && !event.headline) return null;
                      return (
                        <div key={event.headline} className="current-info__event-container">
                          <div className="current-info__event-content">
                            {event.startTime && event.endTime && (
                              <time className="event__time">
                                {/* 4. Replaced <FormattedDate /> and <FormattedTime /> with useFormatter */}
                                {format.dateTime(new Date(event.startTime), {
                                  dateStyle: 'medium'
                                })}{' '}
                                <span>|</span>{' '}
                                {format.dateTime(new Date(event.startTime), {
                                  hour: 'numeric',
                                  minute: 'numeric'
                                })}{' '}
                                –{' '}
                                {format.dateTime(new Date(event.endTime), {
                                  hour: 'numeric',
                                  minute: 'numeric'
                                })}
                              </time>
                            )}
                            <NextLink
                              href={{ pathname: '/universal', query: { uri: event.uri } }}
                              as={event.uri}
                              aria-label={`${t('learnMoreAbout')}"${event.headline}"`}
                            >
                              <Headline tag="h3" tertiary>
                                {event.headline}
                                <span className="current-info__event-stretched-link" />
                              </Headline>
                            </NextLink>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </StyledCurrentInfo>
    </Container>
  );
};

CurrentInfo.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    includeTours: PropTypes.bool
  }),
  dateHeadline: PropTypes.string.isRequired,
  hours: PropTypes.arrayOf(
    PropTypes.shape({
      current: PropTypes.bool,
      day: PropTypes.string,
      from: PropTypes.string,
      to: PropTypes.string,
      open: PropTypes.bool
    })
  ).isRequired,
  headline: PropTypes.string.isRequired,
  copy: PropTypes.string,
  infoCopy: PropTypes.string,
  toursHeadline: PropTypes.string,
  toursCtaLabel: PropTypes.string,
  toursCtaLink: PropTypes.string,
  toursFreeEntrance: PropTypes.string,
  address: PropTypes.shape({
    street: PropTypes.string,
    zipcode: PropTypes.string,
    city: PropTypes.string,
    phone: PropTypes.string
  }).isRequired,
  relevantLinks: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string
    })
  ),
  ticketShop: PropTypes.shape({
    href: PropTypes.string,
    label: PropTypes.string,
    show: PropTypes.bool
  }).isRequired,
  route: PropTypes.shape({
    href: PropTypes.string,
    label: PropTypes.string
  }).isRequired,
  events: PropTypes.arrayOf(PropTypes.shape())
};

export default CurrentInfo;
